import React, { useState, useEffect } from "react";
import { toast } from "react-hot-toast";
import { useProductStore } from "../store/productStore";

export default function AdminProductForm({ 
  formData, 
  setFormData, 
  onSubmit, 
  onCancel, 
  isEditing 
}) {
  const { categories, fetchCategories, addCategory } = useProductStore();
  const [newCategory, setNewCategory] = useState("");
  const [newCategoryAm, setNewCategoryAm] = useState("");
  const [showAddCategory, setShowAddCategory] = useState(false);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddCategory = async () => {
      if (!newCategory.trim()) return;
      try {
          const category = await addCategory(newCategory, newCategoryAm);
          setFormData(prev => ({ ...prev, category_id: category.id }));
          setNewCategory("");
          setNewCategoryAm("");
          setShowAddCategory(false);
          toast.success("Category added successfully");
      } catch (error) {
          toast.error("Error adding category: " + error.message);
      }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error("Image size must be less than 2MB");
        return;
      }
      const reader = new FileReader();
      reader.onload = (event) => {
        setFormData((prev) => ({ ...prev, image: event.target.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="bg-raya-card rounded-lg p-8 mb-8 border border-raya-border shadow-sm">
      <h2 className="text-2xl font-bold mb-6">
        {isEditing ? "Edit Product" : "Add New Product"}
      </h2>
      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-semibold mb-2">
            Product Image
          </label>
          {formData.image && (
            <div className="mb-4">
              <img
                src={formData.image}
                alt="Product preview"
                className="h-40 w-40 object-cover rounded-lg border border-raya-border"
              />
              <button
                type="button"
                onClick={() =>
                  setFormData((prev) => ({ ...prev, image: null }))
                }
                className="mt-2 text-sm text-raya-text-tertiary hover:text-raya-accent hover:underline"
              >
                Remove Image
              </button>
            </div>
          )}
          <input
            type="file"
            accept="image/*"
            onChange={handleImageUpload}
            className="w-full px-4 py-2 border border-raya-border rounded-lg bg-raya-bg-off focus:outline-none focus:ring-2 focus:ring-raya-accent/50"
          />
          <p className="text-xs text-raya-text-tertiary mt-1">
            Max file size: 2MB. Supported formats: JPG, PNG, GIF, WebP
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold mb-2">
                Product Name (English)
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="e.g., Laptop Pro"
                className="w-full px-4 py-2 border border-raya-border rounded-lg bg-raya-bg-off focus:outline-none focus:ring-2 focus:ring-raya-accent/50"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-2">
                Product Name (Amharic)
              </label>
              <input
                type="text"
                name="name_am"
                value={formData.name_am || ""}
                onChange={handleInputChange}
                placeholder="e.g., ላፕቶፕ"
                className="w-full px-4 py-2 border border-raya-border rounded-lg bg-raya-bg-off focus:outline-none focus:ring-2 focus:ring-raya-accent/50"
              />
            </div>
        </div>

        <div>
            <label className="block text-sm font-semibold mb-2">Category</label>
            <div className="flex gap-2">
                <select
                    name="category_id"
                    value={formData.category_id || ""}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-raya-border rounded-lg bg-raya-bg-off focus:outline-none focus:ring-2 focus:ring-raya-accent/50"
                >
                    <option value="">Select Category</option>
                    {categories.map((cat) => (
                        <option key={cat.id} value={cat.id}>
                            {cat.name}
                        </option>
                    ))}
                </select>
                <button
                    type="button"
                    onClick={() => setShowAddCategory(!showAddCategory)}
                    className="bg-raya-bg-off px-4 rounded-lg hover:bg-raya-accent/10 font-bold border border-raya-border text-raya-text-primary"
                >
                    {showAddCategory ? "−" : "+"}
                </button>
            </div>
            {showAddCategory && (
                <div className="mt-2 flex flex-col gap-2">
                    <div className="flex gap-2">
                        <input
                            type="text"
                            value={newCategory}
                            onChange={(e) => setNewCategory(e.target.value)}
                            placeholder="Category Name (English)"
                            className="flex-1 px-4 py-2 border border-raya-border rounded-lg bg-raya-bg-off text-raya-text-primary focus:outline-none focus:ring-2 focus:ring-raya-accent/50"
                        />
                        <input
                            type="text"
                            value={newCategoryAm}
                            onChange={(e) => setNewCategoryAm(e.target.value)}
                            placeholder="Category Name (Amharic)"
                            className="flex-1 px-4 py-2 border border-raya-border rounded-lg bg-raya-bg-off text-raya-text-primary focus:outline-none focus:ring-2 focus:ring-raya-accent/50"
                        />
                    </div>
                    <button
                        type="button"
                        onClick={handleAddCategory}
                        className="w-full bg-raya-accent hover:bg-raya-accent-hover text-black px-4 py-2 rounded-lg font-semibold transition"
                    >
                        Add Category
                    </button>
                </div>
            )}
        </div>

        <div>
          <label className="block text-sm font-semibold mb-2">Price</label>
          <input
            type="number"
            name="price"
            value={formData.price}
            onChange={handleInputChange}
            placeholder="0.00"
            step="0.01"
            className="w-full px-4 py-2 border border-raya-border rounded-lg bg-raya-bg-off focus:outline-none focus:ring-2 focus:ring-raya-accent/50"
          />
        </div>

        <div>
          <label className="block text-sm font-semibold mb-2">
            Quantity in Stock
          </label>
          <input
            type="number"
            name="quantity"
            value={formData.quantity}
            onChange={handleInputChange}
            placeholder="0"
            min="0"
            className="w-full px-4 py-2 border border-raya-border rounded-lg bg-raya-bg-off focus:outline-none focus:ring-2 focus:ring-raya-accent/50"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold mb-2">
                Description (English)
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Product description..."
                rows="4"
                className="w-full px-4 py-2 border border-raya-border rounded-lg bg-raya-bg-off focus:outline-none focus:ring-2 focus:ring-raya-accent/50"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-2">
                Description (Amharic)
              </label>
              <textarea
                name="description_am"
                value={formData.description_am || ""}
                onChange={handleInputChange}
                placeholder="የምርት መግለጫ..."
                rows="4"
                className="w-full px-4 py-2 border border-raya-border rounded-lg bg-raya-bg-off focus:outline-none focus:ring-2 focus:ring-raya-accent/50"
              />
            </div>
        </div>

        <div className="flex gap-4">
          <button
            type="submit"
            className="flex-1 bg-raya-accent hover:bg-raya-accent-hover text-black py-3 rounded-lg font-semibold transition transform hover:scale-[1.02] shadow-lg"
          >
            {isEditing ? "Update Product" : "Add Product"}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 bg-raya-bg-off hover:bg-raya-bg text-raya-text-primary border border-raya-border py-3 rounded-lg font-semibold transition"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
